import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, LabelList,
} from 'recharts';
import api from '../services/api';
import './JDMADashboard.css';

// Tableau de bord JDMA — même grammaire que celui de Google My Business.
//
// ⚠️ Note 0 vs absence de note — deux choses différentes :
//   • `note = 0` est une vraie note (70 des 77 zéros portent un commentaire) :
//     elle compte dans la moyenne et apparaît dans la répartition.
//   • une absence de note (NULL) est comptée dans les avis reçus, hors moyenne.
//
// ⚠️ DEUX FENÊTRES DE TEMPS, et c'est volontaire :
//   • la période choisie (30 jours par défaut) pilote les indicateurs, la
//     répartition et les classements par concession et par établissement ;
//   • l'évolution mensuelle ET le classement par collaborateur restent sur
//     12 mois minimum. Mesuré sur la base réelle : 30 jours ne donnent que
//     ~200 avis notés pour 35 collaborateurs — une MÉDIANE DE 3 AVIS chacun.
//     Un classement nominatif bâti là-dessus serait faux et injuste.
// Chaque bloc annonce sa fenêtre : sans ça, deux chiffres se contrediraient
// à l'écran sans explication.
const PERIODES = [
  { j: 7, label: '7 j' },
  { j: 30, label: '30 j' },
  { j: 90, label: '3 mois' },
  { j: 365, label: '12 mois' },
  { j: 3650, label: 'Tout' },
];
const SEGMENTS = [
  { id: 'all', label: 'APV + VN' },
  { id: 'apv', label: 'APV' },
  { id: 'vn', label: 'VN' },
];

const fr = (n) => (n == null ? '—' : n.toLocaleString('fr-FR'));
const note2 = (n) => (n == null ? '—' : n.toFixed(2).replace('.', ','));
const tonNote = (n) => (n == null ? '' : n >= 4 ? ' jd-g' : n >= 3.5 ? ' jd-o' : ' jd-r');

function Etoiles({ note }) {
  const pleine = Math.round(note || 0);
  return <span className="jd-stars">{'★'.repeat(pleine)}{'☆'.repeat(Math.max(0, 5 - pleine))}</span>;
}

export default function JDMADashboard({ concessions = [] }) {
  const [jours, setJours] = useState(30);
  const [type, setType] = useState('all');
  const [concessionId, setConcessionId] = useState('');
  const [etablissement, setEtablissement] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setErreur('');
    try {
      const params = { jours, type };
      if (concessionId) params.concession_id = concessionId;
      if (etablissement) params.etablissement = etablissement;
      const r = await api.get('/jdma/dashboard', { params });
      setData(r.data);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Chargement du tableau de bord impossible.');
    } finally {
      setLoading(false);
    }
  }, [jours, type, concessionId, etablissement]);

  useEffect(() => { charger(); }, [charger]);

  // La liste des établissements se déduit du tableau lui-même : pas de
  // référentiel à maintenir, et le filtre ne propose que ce qui existe.
  const listeEtabs = useMemo(
    () => (data?.etablissements || []).map(e => e.etablissement).sort(),
    [data]
  );

  const p = data?.periode;
  const prec = data?.precedent;
  const deltaNote = p?.moyenne != null && prec?.moyenne != null ? p.moyenne - prec.moyenne : null;
  const maxRepart = Math.max(1, ...(data?.repartition || []).map(r => r.n));

  const evolution = useMemo(() => (data?.evolution || []).map(e => {
    const [a, m] = e.mois.split('-');
    return { ...e, label: `${m}/${a.slice(2)}` };
  }), [data]);

  // Axe des notes resserré : sur 0–5 la courbe serait plate (l'amplitude réelle
  // tient en trois dixièmes). Les étiquettes portent la valeur exacte, donc
  // l'œil lit la forme sans que le chiffre puisse tromper.
  const domaineNote = useMemo(() => {
    const vals = evolution.map(e => e.moyenne).filter(v => v != null);
    if (!vals.length) return [0, 5];
    const min = Math.min(...vals), max = Math.max(...vals);
    return [Math.max(0, Math.floor((min - 0.15) * 10) / 10), Math.min(5, Math.ceil((max + 0.15) * 10) / 10)];
  }, [evolution]);

  const libellePeriode = jours >= 3650 ? "tout l'historique" : `${jours} derniers jours`;
  const moisLongs = Math.round((data?.meta?.jours_long || 365) / 30);

  return (
    <div className="jd">
      <div className="page-toolbar jd-toolbar">
        <div className="jd-periodes">
          {PERIODES.map(x => (
            <button key={x.j} className={jours === x.j ? 'on' : ''} onClick={() => setJours(x.j)}>{x.label}</button>
          ))}
        </div>
        <select className="page-select" value={concessionId} onChange={e => setConcessionId(e.target.value)}>
          <option value="">Toutes les concessions</option>
          {concessions.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <select className="page-select" value={etablissement} onChange={e => setEtablissement(e.target.value)}>
          <option value="">Tous les établissements</option>
          {listeEtabs.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <div className="jd-segments">
          {SEGMENTS.map(s => (
            <button key={s.id} className={type === s.id ? 'on' : ''} onClick={() => setType(s.id)}>{s.label}</button>
          ))}
        </div>
      </div>

      {erreur && <div className="jd-erreur">{erreur}</div>}
      {loading && !data && <div className="jd-vide">Chargement…</div>}

      {data && (
        <>
          <div className="jd-kpis">
            <div className="jd-kpi jd-hero">
              <div className="jd-lab">Note moyenne</div>
              <div className="jd-val">{note2(p.moyenne)}<small> / 5</small></div>
              <div className="jd-sub">
                <Etoiles note={p.moyenne} />
                {deltaNote != null && (
                  <> &nbsp;<span className={deltaNote >= 0 ? 'jd-up' : 'jd-dn'}>
                    {deltaNote >= 0 ? '▲' : '▼'} {Math.abs(deltaNote).toFixed(2).replace('.', ',')}
                  </span> vs période précédente ({note2(prec.moyenne)})</>
                )}
              </div>
            </div>
            <div className="jd-kpi">
              <div className="jd-lab">Avis reçus</div>
              <div className="jd-val">{fr(p.recus)}</div>
              <div className="jd-sub"><b>{fr(p.notes)}</b> notés · {fr(p.avec_commentaire)} avec commentaire</div>
            </div>
            <div className="jd-kpi">
              <div className="jd-lab">Excellence · 5 étoiles</div>
              <div className="jd-val">{p.pct5}<small> %</small></div>
              <div className="jd-sub">{fr(p.nb5)} avis</div>
              <div className="jd-bar"><i style={{ width: `${p.pct5}%`, background: 'var(--color-success)' }} /></div>
            </div>
            <div className="jd-kpi">
              <div className="jd-lab">Alerte · 3 étoiles et moins</div>
              <div className="jd-val">{p.pct_alerte}<small> %</small></div>
              <div className="jd-sub">{fr(p.nb_alerte)} avis</div>
              <div className="jd-bar"><i style={{ width: `${p.pct_alerte}%`, background: 'var(--color-error)' }} /></div>
            </div>
          </div>

          <div className="jd-cols">
            <div className="jd-card">
              <h3>Évolution mensuelle</h3>
              <div className="jd-csub">
                Note moyenne par mois (courbe) et volume d'avis (barres) — <b>{moisLongs} derniers mois</b>,
                indépendamment du filtre de période.
              </div>
              <div className="jd-chart">
                <ResponsiveContainer width="100%" height={230}>
                  <ComposedChart data={evolution} margin={{ top: 22, right: 8, left: -14, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
                    <XAxis dataKey="label" stroke="var(--text-muted)" fontSize={11} tickLine={false} />
                    <YAxis yAxisId="vol" hide />
                    <YAxis yAxisId="note" domain={domaineNote} stroke="var(--text-muted)" fontSize={11}
                           tickLine={false} axisLine={false} width={38}
                           tickFormatter={v => v.toFixed(1).replace('.', ',')} />
                    <Tooltip
                      formatter={(v, n) => (n === 'Note moyenne' ? [note2(v), n] : [fr(v), 'Avis'])}
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border-default)' }}
                    />
                    <Bar yAxisId="vol" dataKey="n" fill="var(--color-info-light)" radius={[3, 3, 0, 0]} name="Avis" />
                    <Line yAxisId="note" type="monotone" dataKey="moyenne" name="Note moyenne"
                          stroke="var(--color-accent)" strokeWidth={2.5} dot={{ r: 3.5, fill: '#fff', strokeWidth: 2 }}>
                      <LabelList dataKey="moyenne" position="top" offset={9}
                                 formatter={v => note2(v)}
                                 style={{ fontSize: 10.5, fontWeight: 700, fill: 'var(--text-primary)' }} />
                    </Line>
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="jd-card">
              <h3>Répartition des notes</h3>
              <div className="jd-csub">Sur les {fr(p.notes)} avis notés des {libellePeriode}</div>
              <div className="jd-rep">
                {[5, 4, 3, 2, 1, 0].map(n => {
                  const ligne = (data.repartition || []).find(r => r.note === n);
                  const v = ligne?.n || 0;
                  const couleur = n === 5 ? '#16a34a' : n === 4 ? '#65a30d' : n === 3 ? '#f59e0b' : n === 2 ? '#f97316' : '#dc2626';
                  return (
                    <div className="jd-rep-l" key={n}>
                      {/* Le zéro est une note à part entière : on l'écrit, il n'a
                          pas d'étoile à afficher. */}
                      <span className="jd-rep-n">{n === 0 ? <em className="jd-zero">0 étoile</em> : '★'.repeat(n)}</span>
                      <span className="jd-rep-t"><i style={{ width: `${(v / maxRepart) * 100}%`, background: couleur }} /></span>
                      <span className="jd-rep-c">{fr(v)} · {p.notes ? Math.round((v / p.notes) * 100) : 0} %</span>
                    </div>
                  );
                })}
              </div>
              {p.sans_note > 0 && (
                <div className="jd-csub jd-note0">
                  <b>{fr(p.sans_note)} avis sans aucune note</b> — hors moyenne et hors répartition,
                  comptés dans les {fr(p.recus)} reçus. Le <b>0 étoile</b>, lui, est une note :
                  il compte.
                </div>
              )}
              {(data.par_type || []).length > 1 && (
                <div className="jd-types">
                  {data.par_type.map(t => (
                    <span key={t.type} className="jd-type">
                      {t.type} · <b>{fr(t.n)}</b> avis · <span className={`jd-note${tonNote(t.moyenne)}`}>{note2(t.moyenne)}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="jd-cols">
            <div className="jd-card">
              <h3>Par établissement</h3>
              <div className="jd-csub">Sur les {libellePeriode}</div>
              <Classement lignes={data.etablissements} cle="etablissement" vide="Aucun avis sur la période." />
            </div>
            <div className="jd-card">
              <h3>Par concession</h3>
              <div className="jd-csub">Sur les {libellePeriode}</div>
              <Classement lignes={data.concessions} cle="nom" vide="Aucun avis sur la période." />
            </div>
          </div>

          <div className="jd-card">
            <h3>Par collaborateur</h3>
            <div className="jd-csub">
              Sur <b>{moisLongs} mois</b> — et non sur la période choisie : {data.meta.seuil_collaborateur} avis
              minimum pour apparaître, sinon une moyenne calculée sur trois avis passerait pour une performance.
            </div>
            <Classement lignes={data.collaborateurs} cle="collaborateur" jauge
                        vide={`Aucun collaborateur n'atteint ${data.meta.seuil_collaborateur} avis sur la période longue.`} />
          </div>
        </>
      )}
    </div>
  );
}

function Classement({ lignes = [], cle, jauge = false, vide }) {
  if (!lignes.length) return <div className="jd-vide">{vide}</div>;
  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>{cle === 'collaborateur' ? 'Collaborateur' : cle === 'nom' ? 'Concession' : 'Établissement'}</th>
            <th className="jd-num">Avis</th>
            <th className="jd-num">Note</th>
            {jauge && <th>Répartition</th>}
          </tr>
        </thead>
        <tbody>
          {lignes.map(l => (
            <tr key={l[cle]}>
              <td className="jd-nm">{l[cle]}</td>
              <td className="jd-num">{fr(l.n)}</td>
              <td className="jd-num"><span className={`jd-note${tonNote(l.moyenne)}`}>{note2(l.moyenne)}</span></td>
              {jauge && (
                <td>
                  <span className="jd-jauge">
                    <i style={{
                      width: `${Math.max(0, Math.min(100, ((l.moyenne || 0) / 5) * 100))}%`,
                      background: l.moyenne >= 4 ? '#16a34a' : l.moyenne >= 3.5 ? '#f59e0b' : '#dc2626',
                    }} />
                  </span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
